import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Trash2, KeyRound } from 'lucide-react';
import { useUserStore, useToastStore } from '../store';
import { Spinner, Modal, Avatar } from '../components/ui';
import { api } from '../utils/api';

export function UserEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { users, fetchUsers } = useUserStore();
  const { addToast } = useToastStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    display_name: '',
    email: '',
    role: 'Employee' as 'SuperAdmin' | 'Admin' | 'Employee',
    status: 'Active' as 'Pending' | 'Active' | 'Rejected' | 'Inactive',
    phone: '',
  });

  useEffect(() => {
    if (id) {
      loadUser();
    }
  }, [id]);

  const loadUser = async () => {
    try {
      const result = await api.getUsers();
      if (result.success && result.data) {
        const user = result.data.find((u: any) => u.id === id);
        if (user) {
          setFormData({
            display_name: user.display_name || '',
            email: user.email || '',
            role: (user.role || 'Employee') as 'SuperAdmin' | 'Admin' | 'Employee',
            status: (user.status || 'Active') as 'Pending' | 'Active' | 'Rejected' | 'Inactive',
            phone: user.phone || '',
          });
        } else {
          addToast('User not found', 'error');
          navigate('/admin/users');
        }
      }
    } catch (error) {
      console.error('Failed to load user:', error);
      addToast('Failed to load user', 'error');
      navigate('/admin/users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!id) return;
    
    setIsSaving(true);
    try {
      const result = await api.updateUser(id, formData);
      if (result.success) {
        addToast('User updated successfully', 'success');
        fetchUsers();
        navigate('/admin/users');
      } else {
        addToast(result.error || 'Failed to update user', 'error');
      }
    } catch (error) {
      addToast('Failed to update user', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!id) return;
    
    setIsSaving(true);
    try {
      const result = await api.deleteUser(id);
      if (result.success) {
        addToast('User deactivated. Data is preserved; reactivate from User Management if needed.', 'success');
        fetchUsers();
        navigate('/admin/users');
      } else {
        addToast(result.error || 'Failed to deactivate user', 'error');
      }
    } catch (error) {
      addToast('Failed to deactivate user', 'error');
    } finally {
      setIsSaving(false);
      setShowDeleteModal(false);
    }
  };

  const handleResetPassword = async () => {
    if (!id || !newPassword) return;
    
    if (newPassword.length < 6) {
      addToast('Password must be at least 6 characters', 'warning');
      return;
    }
    
    setIsSaving(true);
    try {
      const result = await api.adminResetPassword(id, newPassword);
      if (result.success) {
        addToast('Password reset successfully', 'success');
        setShowResetPasswordModal(false);
        setNewPassword('');
      } else {
        addToast(result.error || 'Failed to reset password', 'error');
      }
    } catch (error) {
      addToast('Failed to reset password', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setShowPassword(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  const user = users.find(u => u.id === id);

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/users')}
          className="btn btn-ghost btn-sm"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Edit User</h1>
          <p className="text-slate-500 mt-1">Update user information and settings</p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="flex items-center gap-4 mb-6">
          <Avatar name={formData.display_name || user?.username || 'User'} size="lg" />
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {formData.display_name || user?.username || 'User'}
            </h2>
            <p className="text-slate-500">{formData.email || user?.email}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="input-label">Display Name</label>
            <input
              type="text"
              value={formData.display_name}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              className="input"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="input-label">Email Address *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input"
              placeholder="user@example.com"
              required
            />
          </div>

          <div>
            <label className="input-label">Role *</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as 'SuperAdmin' | 'Admin' | 'Employee' })}
              className="input"
            >
              <option value="Employee">Employee</option>
              <option value="Admin">Admin</option>
              <option value="SuperAdmin">Super Admin</option>
            </select>
          </div>

          <div>
            <label className="input-label">Status *</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Pending' | 'Active' | 'Rejected' | 'Inactive' })}
              className="input"
            >
              <option value="Pending">Pending</option>
              <option value="Active">Active</option>
              <option value="Rejected">Rejected</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <div>
            <label className="input-label">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input"
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setShowResetPasswordModal(true)}
            className="btn btn-secondary"
          >
            <KeyRound className="w-4 h-4" />
            Reset Password
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="btn btn-danger"
          >
            <Trash2 className="w-4 h-4" />
            Deactivate User
          </button>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="btn btn-primary"
        >
          {isSaving ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Changes
            </>
          )}
        </button>
      </div>

      {/* Reset Password Modal */}
      <Modal
        isOpen={showResetPasswordModal}
        onClose={() => {
          setShowResetPasswordModal(false);
          setNewPassword('');
        }}
        title="Reset Password"
      >
        <div className="space-y-4">
          <p className="text-slate-600">
            Reset password for <strong>{formData.display_name || user?.username}</strong>
          </p>
          
          <div>
            <label className="input-label">New Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input pr-20"
                placeholder="Enter new password"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="p-1.5 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>
          </div>
          
          <button
            type="button"
            onClick={generateRandomPassword}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Generate random password
          </button>

          <div className="flex gap-3 justify-end">
            <button
              onClick={() => {
                setShowResetPasswordModal(false);
                setNewPassword('');
              }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleResetPassword}
              disabled={isSaving || !newPassword}
              className="btn btn-primary"
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Reset Password'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Deactivate Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Deactivate User"
      >
        <div className="space-y-4">
          <p className="text-slate-600">
            Deactivate <strong>{formData.display_name || user?.username}</strong>?
          </p>
          <p className="text-sm text-slate-500">
            They will no longer be able to log in. No data is deleted — you can reactivate them from User Management if needed.
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDeactivate}
              disabled={isSaving}
              className="btn btn-danger"
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Deactivate User'
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

